import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { Alert, Box, Typography } from '@mui/material';
import ConfirmDialog from '../../../../commonComponent/ConfirmDialog';
import { FormattedMessage } from 'react-intl';
import { IconHelp } from '@tabler/icons-react';

/**
 * Grant Access to Technical Support Dialog
 * Uses ConfirmDialog component with custom styling and icon
 */
const GrantAccessDialog = ({ open, onClose, onConfirm, loading = false, errorMessage = '' }) => {
  const theme = useTheme();

  const message = useMemo(
    () => (
      <>
        <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'grey.500', textAlign: 'center' }}>
          <FormattedMessage id="grantAccessDescription1" />
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.6, color: 'grey.500', textAlign: 'center' }}>
          <FormattedMessage id="grantAccessDescription2" />
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 500,
            textAlign: 'center',
            color: theme.palette.grey[800]
          }}
        >
          <FormattedMessage id="grantAccessConfirmation" />
        </Typography>
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [theme.palette.text.primary]
  );

  // Match Add Device modal error styling (addSingleDevice.js — standard severity="error" Alert)
  const errorAlert = useMemo(
    () =>
      errorMessage ? (
        <Alert
          id="grant-access-dialog-error"
          severity="error"
          sx={(theme) => ({
            width: `calc(100% + ${theme.spacing(10)})`,
            maxWidth: 'none',
            marginBottom: 0,
            borderRadius: '0',
            mx: -5,
            boxSizing: 'border-box'
          })}
        >
          {errorMessage}
        </Alert>
      ) : null,
    [errorMessage]
  );

  const titleContent = useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          position: 'relative',
          width: '100%'
        }}
      >
        <Box
          sx={{
            width: 87,
            height: 87,
            borderRadius: '50%',
            backgroundColor: '#FFF8E1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2
          }}
        >
          <IconHelp stroke={1} size="40px" color="#EF6C00" />
        </Box>
        <Typography variant="h1" sx={{ fontWeight: 500, textAlign: 'center' }}>
          <FormattedMessage id="grantAccessToTechnicalSupport" />
        </Typography>
      </Box>
    ),
    []
  );

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      onConfirm={onConfirm}
      topContent={errorAlert}
      title={titleContent}
      message={message}
      cancelText={{ id: 'no' }}
      confirmText={{ id: 'yesGrantAccess' }}
      confirmVariant="contained"
      confirmColor="primary"
      loading={loading}
      disableBackdropClick={loading}
      maxWidth="sm"
      fullWidth
      dialogProps={{
        id: 'grant-access-to-technical-support-dialog'
      }}
      titleProps={{
        sx: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          pt: errorMessage ? 1 : 4,
          pb: 2,
          mt: errorMessage ? 0 : undefined,
          position: 'relative',
          width: '100%'
        }
      }}
      contentProps={{
        sx: {
          px: 5,
          pb: 2,
          alignItems: 'stretch'
        }
      }}
      actionsProps={{
        sx: {
          px: 3,
          pb: 3,
          pt: 2,
          gap: 2,
          justifyContent: 'center'
        }
      }}
    />
  );
};

export default GrantAccessDialog;
